/**
 * Settings → Audit log helpers (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-G).
 * Endpoints: routers/admin_audit_log.py.
 */

export interface AuditEntry {
  id: string;
  created_at: string | null;
  admin_id: string | null;
  admin_email: string | null;
  admin_name: string | null;
  action_type: string;
  target_user_id: string | null;
  target_resource: string | null;
  details: unknown;
  ip_address: string | null;
  user_agent: string | null;
  success: boolean | null;
  error_message: string | null;
}

export interface AuditPage {
  items: AuditEntry[];
  next: { before_at: string; before_id: string } | null;
}

export interface AuditFilterOptions {
  admins: { id: string; email: string; full_name: string | null; active: boolean }[];
  actions: { action_type: string; count: number }[];
  actions_window_days: number;
}

export interface AuditFilters {
  adminId: string;
  action: string;
  target: string;
  from: string;          // yyyy-mm-dd, the viewer's local date
  to: string;            // yyyy-mm-dd, inclusive
  failuresOnly: boolean;
  includeAccess: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function localDateString(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Last 30 days, everything but page views. */
export function defaultFilters(now: Date = new Date()): AuditFilters {
  const from = new Date(now);
  from.setDate(from.getDate() - 30);
  return { adminId: '', action: '', target: '', from: localDateString(from), to: '', failuresOnly: false, includeAccess: false };
}

/** Start of a local calendar day as an ISO instant (times are browser-local, decision 7). */
export function localDayStartIso(day: string, addDays = 0): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + addDays, 0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Query string for the list and export endpoints. "To" is inclusive of that whole day. */
export function filtersToQuery(f: AuditFilters, cursor?: AuditPage['next']): string {
  const q = new URLSearchParams();
  if (f.adminId) q.set('admin_id', f.adminId);
  if (f.action.trim()) q.set('action', f.action.trim());
  if (f.target.trim()) q.set('target', f.target.trim());
  const since = f.from ? localDayStartIso(f.from) : null;
  if (since) q.set('since', since);
  const until = f.to ? localDayStartIso(f.to, 1) : null;
  if (until) q.set('until', until);
  if (f.failuresOnly) q.set('failures_only', 'true');
  if (f.includeAccess) q.set('include_access', 'true');
  if (cursor) {
    q.set('before_at', cursor.before_at);
    q.set('before_id', cursor.before_id);
  }
  return q.toString();
}

/** "admin_setting_updated" → "Admin setting updated"; page views and capability uses named. */
export function actionLabel(action: string): string {
  if (action.startsWith('api_access:')) return `Page view (${action.slice('api_access:'.length)})`;
  if (action.startsWith('capability:')) return `Used ${action.slice('capability:'.length)}`;
  const words = action.replace(/[_:.-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : action;
}

/** One-line summary of the details object: "key: value · key: value", clipped. */
export function detailsSummary(details: unknown, max = 140): string {
  if (details === null || details === undefined || details === '') return '';
  let text: string;
  if (typeof details === 'object' && !Array.isArray(details)) {
    text = Object.entries(details as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join(' · ');
  } else {
    text = typeof details === 'string' ? details : JSON.stringify(details);
  }
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function whoLabel(e: AuditEntry): string {
  return e.admin_name || e.admin_email || (e.admin_id ? e.admin_id.slice(0, 8) : 'System');
}
